import React, { useState, useEffect } from 'react'
import { api } from '../utils/supabase'
import { Database, RefreshCw, FolderOpen, FileText, CheckCircle, AlertCircle, Clock, Settings as SettingsIcon } from 'lucide-react'
import Icon3D from '../components/Icon3D'

export default function Settings() {
  const [config, setConfig] = useState({ auto_sync: false, sync_interval: 60 })
  const [csvFiles, setCsvFiles] = useState([])
  const [csvDir, setCsvDir] = useState('')
  const [message, setMessage] = useState(null)
  const [syncing, setSyncing] = useState(false)

  useEffect(() => { fetchFiles() }, [])

  const fetchFiles = async () => {
    try {
      const res = await api.get('/api/sync/files')
      if (res.success) { setCsvFiles(res.files); setCsvDir(res.dir) }
    } catch (e) { console.error(e) }
  }

  const handleTestConnection = async () => {
    try {
      const res = await api.get('/api/health')
      setMessage({ type: 'success', text: `✅ Servidor OK — ${res.timestamp}` })
    } catch {
      setMessage({ type: 'error', text: '❌ No se pudo conectar al servidor' })
    }
    setTimeout(() => setMessage(null), 4000)
  }

  const handleSync = async () => {
    setSyncing(true)
    try {
      const res = await api.post('/api/sync/manual', {})
      const total = res.summary?.reduce((a, s) => a + (s.inserted || 0), 0) || 0
      setMessage({ type: 'success', text: `✅ Sincronizados ${total} registros de ${res.files} archivos` })
      fetchFiles()
    } catch (e) {
      setMessage({ type: 'error', text: `❌ ${e.message}` })
    } finally {
      setSyncing(false)
      setTimeout(() => setMessage(null), 5000)
    }
  }

  const fmt = (bytes) => bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`
  const fmtDate = (iso) => new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Icon3D icon={SettingsIcon} color="gray" size="md" /> Configuración
        </h1>
        <p className="text-gray-400 text-sm">Ajustes del sistema</p>
      </div>

      {message && (
        <div className={`card p-3 flex items-center gap-2 text-sm ${
          message.type === 'success'
            ? 'bg-emerald-500/10 !border-emerald-500/40 text-emerald-300'
            : 'bg-red-500/10 !border-red-500/40 text-red-300'
        }`}>
          {message.type === 'success'
            ? <CheckCircle className="w-4 h-4 shrink-0" />
            : <AlertCircle className="w-4 h-4 shrink-0" />}
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Carpeta CSV */}
        <div className="lg:col-span-2 card p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Icon3D icon={FolderOpen} color="amber" size="sm" />
              Carpeta de CSVs
            </h3>
            <div className="flex items-center gap-2">
              <button onClick={fetchFiles}
                className="btn btn-ghost !py-1.5 !px-3">
                Actualizar lista
              </button>
              <button onClick={handleSync} disabled={syncing || csvFiles.length === 0}
                className="btn btn-primary !py-1.5 !px-3">
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Sincronizar todos'}
              </button>
            </div>
          </div>

          <div className="mb-3 flex items-center gap-2 text-xs text-gray-500 bg-gray-900/50 rounded-lg px-3 py-2 font-mono">
            <FolderOpen className="w-3.5 h-3.5 text-gray-600 shrink-0" />
            <span className="truncate">{csvDir || './csv-imports'}</span>
          </div>

          <p className="text-xs text-gray-500 mb-3">
            Coloca aquí los archivos CSV. El sistema detecta el tipo automáticamente por las columnas.
          </p>

          {csvFiles.length === 0 ? (
            <div className="text-center py-8 card border-dashed text-gray-500 text-sm">
              No hay archivos CSV en la carpeta
            </div>
          ) : (
            <div className="space-y-1.5">
              {csvFiles.map(f => (
                <div key={f.name} className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-xl hover:bg-white/10 transition">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                    <span className="text-sm text-white truncate">{f.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0 ml-3">
                    <span>{fmt(f.size)}</span>
                    <span className="hidden sm:flex items-center gap-1">
                      <Clock className="w-3 h-3" />{fmtDate(f.modified)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Conexión */}
        <div className="card p-5">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Icon3D icon={Database} color="blue" size="sm" />
            Servidor
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">API:</span>
              <span className="text-amber-400 font-mono">localhost:3000</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Frontend:</span>
              <span className="text-amber-400 font-mono">localhost:5173+</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Base de datos:</span>
              <span className="text-amber-400 font-mono">SQLite local</span>
            </div>
              <button onClick={handleTestConnection}
               className="btn btn-ghost w-full mt-2">
              Probar conexión
            </button>
          </div>
        </div>

        {/* Auto-sync */}
        <div className="card p-5">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Icon3D icon={RefreshCw} color="emerald" size="sm" />
            Sincronización automática
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Auto-sync</p>
                <p className="text-xs text-gray-500">Sincronizar CSVs automáticamente</p>
              </div>
              <button onClick={() => setConfig(c => ({ ...c, auto_sync: !c.auto_sync }))}
                className={`w-11 h-6 rounded-full transition-colors relative ${config.auto_sync ? 'bg-amber-500' : 'bg-gray-600'}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config.auto_sync ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {config.auto_sync && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Intervalo (minutos)</label>
                <input type="number" min="5" max="1440" value={config.sync_interval}
                  onChange={e => setConfig(c => ({ ...c, sync_interval: parseInt(e.target.value) }))}
                  className="input" />
              </div>
            )}
            <p className="text-xs text-gray-600">Configura ENABLE_AUTO_SYNC en .env para activar en producción</p>
          </div>
        </div>

      </div>
    </div>
  )
}
